<template>
    <div class="mypage__member mypage__member--active">
        <div class="mypage__member-tabs">
            <a href="#" class="mypage__member-tab" :class="{ 'mypage__member-tab--active': activeTab === 'routes' }" @click.prevent="activeTab = 'routes'">Turer</a>
            <a href="#" class="mypage__member-tab" :class="{ 'mypage__member-tab--active': activeTab === 'targets' }" @click.prevent="activeTab = 'targets'">Mål</a>
            <a href="#" class="mypage__member-tab" :class="{ 'mypage__member-tab--active': activeTab === 'favourites' }" @click.prevent="activeTab = 'favourites'">Favoritter</a>
        </div>

        <div class="mypage__member-content" :class="{ 'mypage__member-content--active': activeTab === 'routes' }">
            <MyPageMemberSelector
                v-model:member-id="selectedMemberId"
                v-model:competition-id="selectedCompetitionId"
                :family-members="props.familyMembers"
                :competitions="competitions"
            />
            <MyPageRoutesTab
                :current="current"
                :load-all="loadAll"
                :load-all-top="loadAllTop"
                :delete-registration="deleteRegistration"
            />
        </div>

        <div class="mypage__member-content" :class="{ 'mypage__member-content--active': activeTab === 'targets' }">
            <MyPageMemberSelector
                v-model:member-id="selectedMemberId"
                v-model:competition-id="selectedCompetitionId"
                :family-members="props.familyMembers"
                :competitions="competitions"
            />
            <MyPageTargetsTab
                :current="current"
                :submit-targets="submitTargets"
            />
        </div>

        <div class="mypage__member-content" :class="{ 'mypage__member-content--active': activeTab === 'favourites' }">
            <FavouriteList :csrf-token="props.csrfToken" :person-id="props.personId" />
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import FavouriteList from './FavouriteList.vue';
import MyPageMemberSelector from './MyPageMemberSelector.vue';
import MyPageRoutesTab from './MyPageRoutesTab.vue';
import MyPageTargetsTab from './MyPageTargetsTab.vue';

const props = defineProps({
    personId: { type: Number, required: true },
    competitionId: { type: [Number, String], default: '' },
    activeCompetitionName: { type: String, default: '' },
    csrfToken: { type: String, required: true },
    familyMembers: { type: Array, default: () => [] },
});

const selectedMemberId = ref(props.personId);
const selectedCompetitionId = ref(props.competitionId);
const activeTab = ref('routes');

// The "Sesong" dropdown used to be server-rendered into data-competitions;
// fetched over AJAX now instead (Xhr\MyPage\SeasonsController) - see that
// controller/App\Support\SeasonsLookup for why it's not just every season
// the competition has ever run.
const competitions = ref([]);

onMounted(async () => {
    const response = await fetch('/xhr/frontend/competitions');
    const data = await response.json();
    competitions.value = data.competitions || [];
});

// Cache of already-fetched member/year bundles (stats + registrations +
// targets), keyed by "memberId:competitionId" - switching back to a
// previously-viewed combination reuses it instead of refetching. Owned here
// (not in the tab components) since both tabs read from the same bundle and
// mutating actions (delete a registration, update targets) need to update
// it in one place.
const cardCache = reactive({});

function cacheKey(memberId, competitionId) {
    return memberId + ':' + competitionId;
}

const current = computed(() => cardCache[cacheKey(selectedMemberId.value, selectedCompetitionId.value)] || null);

async function load({ showAll = false, showTop = false } = {}) {
    const memberId = selectedMemberId.value;
    const competitionId = selectedCompetitionId.value;
    const params = new URLSearchParams({ competition: competitionId });
    if (showAll) params.set('show_all', '1');
    if (showTop) params.set('show_top', '1');

    const response = await fetch(`/xhr/frontend/users/${memberId}?${params}`);
    const data = await response.json();
    data.showAll = showAll;
    data.showTop = showTop;
    cardCache[cacheKey(memberId, competitionId)] = data;
}

function ensureLoaded() {
    if (!current.value) {
        load();
    }
}

watch([selectedMemberId, selectedCompetitionId], ensureLoaded, { immediate: true });

// RegisterTrip.vue (a separately-mounted Vue app - see mypage.js) dispatches
// this on the window after a successful trip registration. A full-page
// reload used to make this a non-issue; now the cache has to be told
// explicitly that it's stale. Clearing everything and reloading the current
// view is simpler than tracking which member/competition was affected, and
// registrations are infrequent enough that the extra refetch cost doesn't matter.
function onRegistrationAdded() {
    Object.keys(cardCache).forEach((key) => delete cardCache[key]);
    load();
}

onMounted(() => window.addEventListener('mypage:registration-added', onRegistrationAdded));
onUnmounted(() => window.removeEventListener('mypage:registration-added', onRegistrationAdded));

function loadAll() {
    return load({ showAll: true });
}

function loadAllTop() {
    return load({ showTop: true });
}

async function deleteRegistration(reg) {
    const memberId = selectedMemberId.value;
    const competitionId = selectedCompetitionId.value;
    const wasShowAll = current.value?.showAll || false;
    const wasShowTop = current.value?.showTop || false;

    const response = await fetch('/xhr/frontend/users/registrations/delete', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-TOKEN': props.csrfToken,
        },
        body: JSON.stringify({
            person: memberId,
            route: reg.route_id,
            date: reg.route_date,
            competition: competitionId,
            show_all: wasShowAll,
            show_top: wasShowTop,
        }),
    });

    if (response.ok) {
        const data = await response.json();
        data.showAll = wasShowAll;
        data.showTop = wasShowTop;
        cardCache[cacheKey(memberId, competitionId)] = data;
    }
}

async function submitTargets(values) {
    const response = await fetch(`/xhr/frontend/users/${selectedMemberId.value}/targets`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-TOKEN': props.csrfToken,
        },
        body: JSON.stringify({
            competition: selectedCompetitionId.value,
            targets_destinations: values.destinations,
            targets_registrations: values.registrations,
            targets_distance: values.distance,
            targets_elevation: values.elevation,
        }),
    });
    const data = await response.json();

    const key = cacheKey(selectedMemberId.value, selectedCompetitionId.value);
    if (cardCache[key]) {
        cardCache[key].targets = data.targets;
    }
}
</script>
